import { SortStatus } from '@dixa/lib-shared-habla';
import {
  TableInstrument,
  type TableInstrumentDataCustomFormats,
  type TableInstrumentProps,
} from 'lib-analytics-ui';
import { ampli } from 'lib-shared-tracking';
import { memo, useCallback, useMemo } from 'react';

import { useAnalyticsState } from 'analytics/components/panels/utils/useAnalyticsState/useAnalyticsState';
import { ConversationId } from 'components/conversation';

export type TableInstrumentRendererProps = Pick<
  TableInstrumentProps,
  'instrument' | 'panel' | 'onReadInstruments'
>;

/**
 * A component that abstracts application-specific logic related to rendering a
 * table instrument, such as tracking callbacks or rendering custom components.
 */
export const TableInstrumentRenderer = memo(function TableInstrumentRenderer(
  props: TableInstrumentRendererProps,
): JSX.Element {
  const { panel } = props;

  const {
    dashboard,
    meta: { openLinksInNewTab: openInNewTab },
    settings: { timezone },
  } = useAnalyticsState(panel);

  const onLoadAll = useCallback(() => {
    ampli.analyticsTableLoadAllClicked({
      panel: panel.title,
      dashboard: dashboard.title,
    });
  }, [dashboard.title, panel.title]);

  const onSortChange = useCallback(
    (sortStatus: SortStatus | null) => {
      ampli.analyticsTableSortUpdated({
        column: (sortStatus as SortStatus)?.sortBy as string,
        panel: panel.title,
        dashboard: dashboard.title,
      });
    },
    [dashboard.title, panel.title],
  );

  const customFormats = useMemo(
    (): TableInstrumentDataCustomFormats => ({
      csid: ({ value }) => {
        const csid = value as number;

        return (
          <ConversationId
            csid={csid}
            actions={{
              link: {
                openInNewTab,
                onClick: () => {
                  ampli.analyticsConversationPreviewClicked({
                    csid: csid,
                    dashboard: dashboard.title,
                    panel: panel.title,
                  });
                },
              },
              preview: {
                onClick: () => {
                  ampli.analyticsConversationLinkClicked({
                    csid,
                    dashboard: dashboard.title,
                    panel: panel.title,
                  });
                },
              },
            }}
          />
        );
      },
    }),
    [dashboard.title, openInNewTab, panel.title],
  );

  return (
    <TableInstrument
      {...props}
      timezone={timezone}
      onLoadAll={onLoadAll}
      onSortChange={onSortChange}
      customFormats={customFormats}
    />
  );
});
